
import { defineComponent, ref, computed, watch, nextTick, PropType } from 'vue'
import FactorGroup from './FactorGroup.vue'
import FactorDescDialog from './FactorDescDialog.vue'
import {
  formatFactorSelectedListLabel,
  stripWatchwordSelectionDescript,
} from '@/utils/starFactorLabel'

// 从原始树中提取所有叶子因子（attrType !== null），附带 rootName
function extractFactors(nodes: any[], rootName = ''): any[] {
  const result: any[] = []
  for (const node of nodes) {
    const name = rootName || node.attrName
    if (node.attrType !== null && node.attrType !== undefined) {
      result.push({ ...node, rootName: name })
    }
    if (node.childAttrList?.length) {
      result.push(...extractFactors(node.childAttrList, name))
    }
  }
  return result
}

export default defineComponent({
  name: 'FactorSelector',
  components: { FactorGroup, FactorDescDialog },
  props: {
    factorData: { type: Array as PropType<any[]>, default: () => [] },
    initialSelection: { type: Array as PropType<any[]>, default: () => [] },
    isLight: Boolean,
    colors: { type: Object as PropType<Record<string, string>>, required: true },
    headers: { type: Object as PropType<Record<string, string>>, default: () => ({}) }
  },
  emits: ['change'],
  setup(props, { emit }) {
    const activeTab = ref('')
    const selectedFactors = ref<any[]>([])
    const searchKeyword = ref('')
    const showDropdown = ref(false)
    const searchActiveIndex = ref(-1)
    const searchInputRef = ref<HTMLInputElement | null>(null)
    const searchDropdownRef = ref<HTMLElement | null>(null)
    const highlightCode = ref('')

    const dropdownTagStyle = computed(() => ({
      color: props.isLight ? props.colors.textMuted : 'rgba(255, 255, 255, 0.55)',
    }))

    // 一级 tab 树
    const factorTree = computed(() => props.factorData || [])

    // 初始化默认选中第一个 tab
    watch(factorTree, (val) => {
      if (val.length && !activeTab.value) {
        activeTab.value = val[0].attrCode
      }
    }, { immediate: true })

    let appliedInitialKey = ''
    watch(() => props.initialSelection, (val) => {
      if (!val?.length) return
      const key = val.map((f: any) => f.attrCode).join('|')
      if (key === appliedInitialKey) return
      appliedInitialKey = key
      selectedFactors.value = val.map((f: any) => ({ ...f }))
      emit('change', selectedFactors.value)
    }, { immediate: true, deep: true })

    const currentTab = computed(() =>
      factorTree.value.find((t: any) => t.attrCode === activeTab.value)
    )

    // 已选 code 集合，方便快速判断
    const selectedCodes = computed(() => new Set(selectedFactors.value.map((f: any) => f.attrCode)))

    // 已选因子 Map，用于回显 selectedOptions
    const selectedMap = computed(() => new Map(selectedFactors.value.map((f: any) => [f.attrCode, f])))

    // 所有叶子因子（用于搜索）
    const allFactors = computed(() => extractFactors(factorTree.value))

    // 搜索结果
    const searchResults = computed(() => {
      if (!searchKeyword.value.trim()) return []
      const kw = searchKeyword.value.trim().toLowerCase()
      return allFactors.value.filter((f: any) => f.attrName?.toLowerCase().includes(kw)).slice(0, 20)
    })

    const showDescDialog = ref(false)
    const descDialogTab = ref<any>(null)

    const openDescDialog = (tab: any) => {
      descDialogTab.value = tab
      showDescDialog.value = true
    }

    // 判断 tab 是否有可展示的说明内容
    const tabHasDesc = (tab: any): boolean => {
      const check = (nodes: any[]): boolean => nodes.some((n: any) => {
        if (n.attrType !== null && n.attrType !== undefined) {
          // 有子选项且子选项中有说明才算有描述
          const childrenHaveDesc = (n.childAttrList || []).some((c: any) => c.attrDescShowStatus && c.attrDesc)
          return childrenHaveDesc || !!(n.attrDescShowStatus && n.attrDesc)
        }
        return check(n.childAttrList || [])
      })
      return check(tab.childAttrList || [])
    }

    const syncSearchActiveIndex = () => {
      const n = searchResults.value.length
      if (!n || !showDropdown.value) {
        searchActiveIndex.value = -1
        return
      }
      if (searchActiveIndex.value < 0 || searchActiveIndex.value >= n) {
        searchActiveIndex.value = 0
      }
    }

    watch(searchResults, syncSearchActiveIndex)

    const scrollSearchActiveIntoView = () => {
      nextTick(() => {
        const el = searchDropdownRef.value?.querySelector(
          '.fs-dropdown-item--active',
        ) as HTMLElement | null
        el?.scrollIntoView({ block: 'nearest' })
      })
    }

    const onSearch = () => {
      showDropdown.value = true
      syncSearchActiveIndex()
    }

    const onSearchFocus = () => {
      showDropdown.value = true
      syncSearchActiveIndex()
    }

    const onBlur = () => {
      setTimeout(() => {
        showDropdown.value = false
        searchActiveIndex.value = -1
      }, 150)
    }

    const onSearchKeydown = (e: KeyboardEvent) => {
      const list = searchResults.value
      if (e.key === 'Escape') {
        showDropdown.value = false
        searchActiveIndex.value = -1
        return
      }
      if (!list.length) return

      showDropdown.value = true

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (searchActiveIndex.value < 0) searchActiveIndex.value = 0
        else searchActiveIndex.value = (searchActiveIndex.value + 1) % list.length
        scrollSearchActiveIntoView()
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (searchActiveIndex.value < 0) searchActiveIndex.value = list.length - 1
        else {
          searchActiveIndex.value =
            searchActiveIndex.value <= 0 ? list.length - 1 : searchActiveIndex.value - 1
        }
        scrollSearchActiveIntoView()
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const idx = searchActiveIndex.value >= 0 ? searchActiveIndex.value : 0
        selectFromSearch(list[idx])
      }
    }

    // 获取 tab 的因子说明（子因子 attrDesc 合集）
    const getTabDesc = (tab: any): string => {
      const descs = (tab.childAttrList || [])
        .filter((c: any) => c.attrType !== null && c.attrType !== undefined && c.attrDesc)
        .map((c: any) => c.attrDesc as string)
      return descs.join('；')
    }

    const selectFromSearch = (item: any) => {
      showDropdown.value = false
      searchActiveIndex.value = -1
      searchKeyword.value = ''
      // 切换到对应的一级 tab
      const root = factorTree.value.find((t: any) => t.attrName === item.rootName)
      if (root) activeTab.value = root.attrCode
      // 高亮定位
      highlightCode.value = item.attrCode
      setTimeout(() => { highlightCode.value = '' }, 2000)
    }

    const onToggleFactor = (factor: any) => {
      if (factor._tagRemove) {
        selectedFactors.value = selectedFactors.value.filter((f: any) => f.attrCode !== factor.attrCode)
        emit('change', selectedFactors.value)
        return
      }
      let next = factor
      const idx = selectedFactors.value.findIndex((f: any) => f.attrCode === factor.attrCode)
      const prev = idx >= 0 ? selectedFactors.value[idx] : null
      const userEdited =
        factor._userModified ||
        (prev &&
          (JSON.stringify((factor.selectedOptions || []).map((o: any) => o.attrCode)) !==
            JSON.stringify((prev.selectedOptions || []).map((o: any) => o.attrCode)) ||
            JSON.stringify(factor.selectedRange) !== JSON.stringify(prev.selectedRange) ||
            factor.unionMode !== prev.unionMode))
      if (userEdited && (factor.selectedOptions?.length || factor.selectedRange || factor._confirmed)) {
        next = stripWatchwordSelectionDescript(factor)
      }
      if (idx >= 0) {
        // attrType 7 或已确认的因子：始终更新不删除
        if (next.attrType === 7 || next._confirmed || next.selectedOptions?.length || next.selectedRange) {
          selectedFactors.value.splice(idx, 1, next)
        } else {
          selectedFactors.value.splice(idx, 1)
        }
      } else {
        selectedFactors.value.unshift(next)
      }
      emit('change', selectedFactors.value)
    }

    const removeSelected = (item: any) => {
      selectedFactors.value = selectedFactors.value.filter((f: any) => f.attrCode !== item.attrCode)
      emit('change', selectedFactors.value)
    }

    const formatSelectedLabel = (item: any) => formatFactorSelectedListLabel(item)

    return {
      activeTab, factorTree, currentTab,
      selectedFactors, selectedCodes, selectedMap,
      searchKeyword, searchResults, showDropdown,
      highlightCode,
      searchActiveIndex, searchInputRef, searchDropdownRef, dropdownTagStyle,
      onSearch, onSearchFocus, onBlur, onSearchKeydown, selectFromSearch,
      onToggleFactor, removeSelected, formatSelectedLabel,
      getTabDesc, tabHasDesc,
      showDescDialog, descDialogTab, openDescDialog
    }
  }
})
